import get from 'lodash/get';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useField, useForm } from 'react-final-form';

import { getDaysInMonth } from '@ocx/utils-date';

import { DateFieldGroup } from './date-field-group';

type DateFieldProps = {
  fieldNamePrefix?: string;
  disabled?: boolean;
  minYear?: number;
  maxYear?: number;
};

export const DateField = memo(({ fieldNamePrefix = 'date', disabled, minYear, maxYear }: DateFieldProps) => {
  const monthFieldName = useMemo(() => `${fieldNamePrefix}.month`, [fieldNamePrefix]);
  const dateFieldName = useMemo(() => `${fieldNamePrefix}.date`, [fieldNamePrefix]);
  const yearFieldName = useMemo(() => `${fieldNamePrefix}.year`, [fieldNamePrefix]);
  const [maxDate, setMaxDate] = useState(31);
  const dateFieldRef = useRef<HTMLDivElement>(null);
  const yearFieldRef = useRef<HTMLDivElement>(null);

  const {
    input: { value: year },
  } = useField(yearFieldName);
  const {
    input: { value: month },
  } = useField(monthFieldName);
  const {
    input: { value: date },
  } = useField(dateFieldName);
  const form = useForm();
  const formState = form.getState();

  useEffect(() => {
    if (month && date) {
      const selectedDate = new Date();
      selectedDate.setMonth(month - 1);
      if (year) {
        selectedDate.setFullYear(year);
      }

      const daysInMonth = getDaysInMonth(selectedDate);
      if (date > daysInMonth) {
        form.change(dateFieldName, daysInMonth);
      }
      setMaxDate(daysInMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => {
    if (!formState.dirty || !month) {
      return;
    }
    const value = month.toString();
    const [firstCharacter] = value.split('');
    if (!get(formState.errors, monthFieldName) && (value.length === 2 || Number(firstCharacter) > 1)) {
      // TODO: form.focus(dateFieldName); doesn't work
      // https://github.com/final-form/react-final-form/issues/872
      dateFieldRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => {
    if (!formState.dirty || !date) {
      return;
    }
    const value = date.toString();
    const [firstCharacter] = value.split('');

    if (!get(formState.errors, dateFieldName) && (value.length === 2 || Number(firstCharacter) > 3)) {
      yearFieldRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <DateFieldGroup
      minYear={minYear}
      maxYear={maxYear}
      maxDate={maxDate}
      dateFieldRef={dateFieldRef}
      yearFieldRef={yearFieldRef}
      dateFieldName={dateFieldName}
      yearFieldName={yearFieldName}
      monthFieldName={monthFieldName}
      disabled={disabled}
    />
  );
});
